import "./SupportForm.css";
import { useContext, useEffect, useId, useState } from "react";

import DynamicFields from "../DynamicFields/DynamicFields";
import {
  DEFAULT_SUPPORT_ICON,
  SUPPORT_CATEGORY_ICONS,
} from "../constants/icons";
import { getText } from "../helpers/supportHelpers";
import { fetchStoresAPI } from "../../../../utils/api/stores";
import { Context } from "../../../../context/Context";
import { Select, Button } from "../../../ui";

const TEXT = {
  ar: {
    cancel: "إلغاء",
    submit: "إرسال الطلب",
    submitting: "جاري الإرسال...",
    store: "اختر المتجر*",
    branch: "اختر الفرع*",
    allBranches: "كل الفروع",
    storeRequired: "اختر المتجر.",
    missing: (names) => `أكمل الحقول المطلوبة: ${names}`,
    storesFailed: "تعذر تحميل المتاجر.",
  },
  en: {
    cancel: "Cancel",
    submit: "Submit Request",
    submitting: "Submitting...",
    store: "Choose store*",
    branch: "Choose branch*",
    allBranches: "All branches",
    storeRequired: "Choose a store.",
    missing: (names) => `Complete the required fields: ${names}`,
    storesFailed: "Stores could not be loaded.",
  },
};

const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === false ||
  String(value).trim() === "";

const SupportForm = ({ category, loading, error, onCancel, onSubmit }) => {
  const { lang, storeAccess } = useContext(Context);
  const t = TEXT[lang] || TEXT.ar;
  const [formValues, setFormValues] = useState({});
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [validationError, setValidationError] = useState("");
  const [storesError, setStoresError] = useState("");
  const errorId = useId();

  const storesOptions = stores.map((s) => ({
    value: s,
    label: s.businessName,
  }));
  const branchOptions = [
    { value: "all", label: t.allBranches },
    ...(selectedStore?.branches?.map((b) => ({
      value: b,
      label: b.name,
    })) || []),
  ];

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("auth-token");
    if (!token) return undefined;
    fetchStoresAPI(token, storeAccess)
      .then((result) => {
        if (!active) return;
        if (result) setStores(result);
        else setStoresError(t.storesFailed);
      })
      .catch((err) => {
        console.error("Fetching failed restaurants:", err);
        if (active) setStoresError(t.storesFailed);
      });
    return () => {
      active = false;
    };
    // Load once when the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = SUPPORT_CATEGORY_ICONS[category?.code] || DEFAULT_SUPPORT_ICON;

  // Category-specific answers reset when the category changes; the chosen
  // store/branch are kept.
  useEffect(() => {
    setFormValues({});
    setValidationError("");
  }, [category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    // Submitting without a store used to crash (`selectedStore._id`).
    if (!selectedStore?._id) {
      setValidationError(t.storeRequired);
      return;
    }

    // Native `required` doesn't cover the custom Select fields.
    const missing = (category?.formFields || [])
      .filter((field) => field?.required && field?.key && isEmpty(formValues[field.key]))
      .map((field) => getText(field?.label, lang) || field.key);
    if (missing.length) {
      setValidationError(t.missing(missing.join("، ")));
      return;
    }

    setValidationError("");
    onSubmit(formValues, selectedStore._id, selectedBranch?._id);
  };

  const shownError = validationError || error || storesError;

  return (
    <form
      className="support-ticket-form"
      onSubmit={handleSubmit}
      aria-describedby={shownError ? errorId : undefined}
    >
      <div className="support-form-title">
        <div className="support-form-icon" aria-hidden="true">
          <Icon size={18} strokeWidth={2} />
        </div>

        <span>{getText(category?.name, lang)}</span>
      </div>

      <Select
        label={t.store}
        value={selectedStore}
        options={storesOptions}
        onChange={(o) => {
          setSelectedStore(o.target.value);
          setSelectedBranch("all");
          setValidationError("");
        }}
      />
      {selectedStore && (
        <Select
          label={t.branch}
          value={selectedBranch}
          options={branchOptions}
          onChange={(o) => setSelectedBranch(o.target.value)}
        />
      )}

      <div className="support-form-grid">
        <DynamicFields
          fields={category?.formFields || []}
          values={formValues}
          onChange={(values) => {
            setFormValues(values);
            if (validationError) setValidationError("");
          }}
          lang={lang}
        />
      </div>

      {shownError && (
        <p className="support-action-error" id={errorId} role="alert">
          {shownError}
        </p>
      )}

      <div className="support-form-actions">
        <Button
          type="button"
          variant="danger"
          appearance="soft"
          onClick={onCancel}
          disabled={loading}
        >
          {t.cancel}
        </Button>

        <Button
          type="submit"
          variant="primary"
          appearance="filled"
          disabled={loading}
        >
          {loading ? t.submitting : t.submit}
        </Button>
      </div>
    </form>
  );
};

export default SupportForm;
